import type { StepDefinition, FieldDefinition } from '@klaro7/shared-types';
import { Input } from '@klaro7/ui';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DynamicStepRendererProps {
  stepDef: StepDefinition;
  data: Record<string, unknown>;
  errors: Record<string, string>;
  onChange: (field: string, value: unknown) => void;
}

// ---------------------------------------------------------------------------
// Lingua types + level options
// ---------------------------------------------------------------------------

interface LinguaValue {
  lingua: string;
  livello: string;
}

const LINGUA_LIVELLI = [
  { value: 'base', label: 'Base' },
  { value: 'intermedio', label: 'Intermedio' },
  { value: 'fluente', label: 'Fluente' },
  { value: 'madrelingua', label: 'Madrelingua' },
];

// ---------------------------------------------------------------------------
// Field renderer
// ---------------------------------------------------------------------------

function DynamicField({
  field,
  value,
  error,
  onChange,
}: {
  field: FieldDefinition;
  value: unknown;
  error?: string;
  onChange: (value: unknown) => void;
}) {
  const borderClass = error
    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
    : 'border-neutral-300 focus:border-primary-500';

  switch (field.type) {
    case 'text':
    case 'tel': {
      return (
        <Input
          label={`${field.label}${field.required ? ' *' : ''}`}
          type={field.type === 'tel' ? 'tel' : 'text'}
          value={(value as string) || ''}
          onChange={(e) => onChange(e.target.value)}
          error={error}
          placeholder={field.placeholder}
        />
      );
    }

    case 'email': {
      return (
        <Input
          label={`${field.label}${field.required ? ' *' : ''}`}
          type="email"
          value={(value as string) || ''}
          onChange={(e) => onChange(e.target.value)}
          error={error}
          placeholder={field.placeholder}
        />
      );
    }

    case 'textarea': {
      const strValue = (value as string) || '';
      const minLen = field.validation?.minLength;
      const maxLen = field.validation?.maxLength;
      const charCount = strValue.length;
      const belowMin = minLen ? charCount < minLen : false;

      return (
        <div>
          <label className="mb-1.5 block text-sm font-medium text-neutral-700">
            {field.label}
            {field.required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
          <textarea
            value={strValue}
            onChange={(e) => onChange(e.target.value)}
            rows={4}
            maxLength={maxLen}
            placeholder={field.placeholder}
            className={`w-full rounded-lg border px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-0 transition-colors duration-200 ${borderClass}`}
          />
          <div className="flex justify-between mt-1">
            {error && <p className="text-sm text-red-500">{error}</p>}
            {minLen != null && (
              <p className={`text-xs ml-auto ${belowMin ? 'text-red-500 font-medium' : 'text-neutral-400'}`}>
                {charCount}/{minLen} caratteri minimi
              </p>
            )}
          </div>
        </div>
      );
    }

    case 'select': {
      const options = field.options || [];
      return (
        <div>
          <label className="mb-1.5 block text-sm font-medium text-neutral-700">
            {field.label}
            {field.required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
          <select
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value)}
            className={`w-full h-10 rounded-lg border px-3 text-sm text-neutral-900 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-0 transition-colors duration-200 ${
              error ? 'border-red-500' : 'border-neutral-300'
            }`}
          >
            <option value="">Seleziona...</option>
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
        </div>
      );
    }

    case 'radio': {
      const options = field.options || [];
      return (
        <div>
          <label className="mb-1.5 block text-sm font-medium text-neutral-700">
            {field.label}
            {field.required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
          <div className="flex flex-wrap gap-3 mt-1">
            {options.map((opt) => (
              <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name={field.key}
                  value={opt.value}
                  checked={(value as string) === opt.value}
                  onChange={() => onChange(opt.value)}
                  className="h-4 w-4 text-primary-600 border-neutral-300 focus:ring-primary-500"
                />
                {opt.label}
              </label>
            ))}
          </div>
          {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
        </div>
      );
    }

    case 'checkbox-group': {
      const options = field.options || [];
      const selected = (value as string[]) || [];
      const toggle = (val: string) => {
        if (selected.includes(val)) {
          onChange(selected.filter((v) => v !== val));
        } else {
          onChange([...selected, val]);
        }
      };

      return (
        <div>
          <label className="mb-1.5 block text-sm font-medium text-neutral-700">
            {field.label}
            {field.required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
          <div className="flex flex-wrap gap-3 mt-1">
            {options.map((opt) => (
              <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected.includes(opt.value)}
                  onChange={() => toggle(opt.value)}
                  className="h-4 w-4 rounded text-primary-600 border-neutral-300 focus:ring-primary-500"
                />
                {opt.label}
              </label>
            ))}
          </div>
          {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
        </div>
      );
    }

    case 'boolean': {
      return (
        <div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={(value as boolean) || false}
              onChange={(e) => onChange(e.target.checked)}
              className="h-4 w-4 rounded text-primary-600 border-neutral-300 focus:ring-primary-500"
            />
            <span className="font-medium text-neutral-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-0.5">*</span>}
            </span>
          </label>
          {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
        </div>
      );
    }

    case 'range': {
      const min = field.validation?.min ?? 1;
      const max = field.validation?.max ?? 5;
      const numValue = (value as number) || min;

      return (
        <div>
          <label className="mb-1.5 block text-sm font-medium text-neutral-700">
            {field.label}
            {field.required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
          <div className="flex items-center gap-4 mt-1">
            <input
              type="range"
              min={min}
              max={max}
              step={1}
              value={numValue}
              onChange={(e) => onChange(Number(e.target.value))}
              className="flex-1 h-2 accent-primary-600"
            />
            <span className="text-sm font-semibold text-primary-600 min-w-[2ch] text-center">
              {numValue}
            </span>
          </div>
          {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
        </div>
      );
    }

    case 'lingue': {
      const lingue = (value as LinguaValue[]) || [{ lingua: '', livello: 'base' }];

      const addLingua = () => onChange([...lingue, { lingua: '', livello: 'base' }]);
      const removeLingua = (idx: number) => onChange(lingue.filter((_: LinguaValue, i: number) => i !== idx));
      const updateLingua = (idx: number, key: 'lingua' | 'livello', val: string) => {
        const updated = lingue.map((item, i) =>
          i === idx ? { ...item, [key]: val } : item,
        );
        onChange(updated);
      };

      return (
        <div>
          <label className="mb-1.5 block text-sm font-medium text-neutral-700">
            {field.label}
            {field.required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
          <div className="space-y-2 mt-1">
            {lingue.map((l: LinguaValue, idx: number) => (
              <div key={idx} className="flex gap-2 items-center">
                <input
                  type="text"
                  placeholder="Lingua"
                  value={l.lingua}
                  onChange={(e) => updateLingua(idx, 'lingua', e.target.value)}
                  className="flex-1 h-10 rounded-lg border border-neutral-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <select
                  value={l.livello}
                  onChange={(e) => updateLingua(idx, 'livello', e.target.value)}
                  className="h-10 rounded-lg border border-neutral-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {LINGUA_LIVELLI.map((lv) => (
                    <option key={lv.value} value={lv.value}>
                      {lv.label}
                    </option>
                  ))}
                </select>
                {lingue.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeLingua(idx)}
                    className="h-10 w-10 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addLingua}
            className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            + Aggiungi lingua
          </button>
          {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
        </div>
      );
    }

    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function DynamicStepRenderer({ stepDef, data, errors, onChange }: DynamicStepRendererProps) {
  return (
    <div className="space-y-5">
      <h2 className="text-xl font-semibold text-neutral-900">{stepDef.name}</h2>
      <p className="text-sm text-neutral-500">{stepDef.description}</p>

      {stepDef.fields.map((field) => (
        <DynamicField
          key={field.key}
          field={field}
          value={data[field.key]}
          error={errors[field.key]}
          onChange={(val) => onChange(field.key, val)}
        />
      ))}
    </div>
  );
}
